import { useMemo } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { AlertTriangle, RefreshCw, ShieldCheck } from 'lucide-react';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { DataTable, DataTableSortHeader } from '@/components/ui/data-table';
import { useToast } from '@/hooks/useToast';

import {
  useDatasetClassification,
  useReclassifyDataset,
} from '../hooks/useDatasetClassification';
import type { ColumnClassificationResult } from '../types/classification.types';
import { isStale } from '../utils/staleness';
import { StalenessBadge } from './StalenessBadge';

interface ClassificationSectionProps {
  datasetId: string | undefined;
}

/**
 * Status colors are semantic tokens only — Tailwind palette levels break dark
 * mode. PII/PHI/PCI and `restricted` are the under-classification risk the
 * audit cares about, so they read destructive.
 */
const TONE: Record<string, string> = {
  pii: 'border-destructive/30 bg-destructive/10 text-destructive',
  phi: 'border-destructive/30 bg-destructive/10 text-destructive',
  pci: 'border-destructive/30 bg-destructive/10 text-destructive',
  restricted: 'border-destructive/30 bg-destructive/10 text-destructive',
  critical: 'border-destructive/30 bg-destructive/10 text-destructive',
  confidential: 'border-warning/30 bg-warning/10 text-warning',
  high: 'border-warning/30 bg-warning/10 text-warning',
  internal: 'border-info/30 bg-info/10 text-info',
  medium: 'border-info/30 bg-info/10 text-info',
  public: 'border-success/30 bg-success/10 text-success',
  low: 'border-success/30 bg-success/10 text-success',
};

function toneFor(value: string): string {
  return TONE[value?.toLowerCase()] ?? 'bg-muted text-muted-foreground';
}

function titleCase(value: string): string {
  if (!value) return '—';
  const upper = value.toUpperCase();
  if (['PII', 'PHI', 'PCI'].includes(upper)) return upper;
  return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
}

/** Marks a value a steward set by hand rather than the classifier. */
function ManualBadge() {
  return (
    <Badge
      variant="outline"
      title="Set by a steward; re-classification never overwrites it"
      className="ml-2 border-primary/30 bg-primary/10 text-primary"
    >
      Manual
    </Badge>
  );
}

/**
 * Stored per-column classification verdicts for a dataset, with a staleness
 * indicator for rows produced by an older classifier (#1481).
 */
export function ClassificationSection({ datasetId }: ClassificationSectionProps) {
  const { toast } = useToast();
  const { data, isLoading, error } = useDatasetClassification(datasetId);
  const reclassify = useReclassifyDataset(datasetId);

  const currentVersion = data?.current_classifier_version ?? '';
  const columns = data?.column_classifications ?? [];

  const staleCount = useMemo(
    () =>
      columns.filter((c) => isStale(c.metadata?.classifier_version, currentVersion))
        .length,
    [columns, currentVersion]
  );

  const runReclassify = () => {
    reclassify.mutate(undefined, {
      onSuccess: () =>
        toast({
          title: 'Re-classified',
          description: `Classification refreshed for "${data?.dataset_name ?? 'this dataset'}".`,
        }),
      onError: (err: unknown) =>
        toast({
          title: 'Re-classify failed',
          description: (err as Error)?.message ?? 'Unknown error',
          variant: 'destructive',
        }),
    });
  };

  const tableColumns: ColumnDef<ColumnClassificationResult, unknown>[] = useMemo(
    () => [
      {
        accessorKey: 'column_name',
        header: ({ column }) => (
          <DataTableSortHeader column={column}>Column</DataTableSortHeader>
        ),
        cell: ({ row }) => (
          <span className="font-mono text-xs">{row.original.column_name}</span>
        ),
      },
      {
        accessorKey: 'classification',
        header: ({ column }) => (
          <DataTableSortHeader column={column}>Classification</DataTableSortHeader>
        ),
        cell: ({ row }) => (
          <span className="flex items-center">
            <Badge variant="outline" className={toneFor(row.original.classification)}>
              {titleCase(row.original.classification)}
            </Badge>
            {row.original.metadata?.manual_classification ? <ManualBadge /> : null}
          </span>
        ),
      },
      {
        accessorKey: 'sensitivity',
        header: ({ column }) => (
          <DataTableSortHeader column={column}>Sensitivity</DataTableSortHeader>
        ),
        cell: ({ row }) => (
          <span className="flex items-center">
            <Badge variant="outline" className={toneFor(row.original.sensitivity)}>
              {titleCase(row.original.sensitivity)}
            </Badge>
            {row.original.metadata?.manual_sensitivity_level ? <ManualBadge /> : null}
          </span>
        ),
      },
      {
        id: 'pii_types',
        header: 'PII Types',
        cell: ({ row }) =>
          row.original.pii_types?.length ? (
            <span className="flex flex-wrap gap-1">
              {row.original.pii_types.map((t) => (
                <Badge key={t} variant="secondary" className="text-xs">
                  {t}
                </Badge>
              ))}
            </span>
          ) : (
            <span className="text-muted-foreground">—</span>
          ),
      },
      {
        accessorKey: 'confidence',
        header: ({ column }) => (
          <DataTableSortHeader column={column}>Confidence</DataTableSortHeader>
        ),
        cell: ({ row }) => <span>{row.original.confidence?.toFixed(2) ?? '—'}</span>,
      },
      {
        id: 'classifier_version',
        header: 'Classifier Version',
        cell: ({ row }) => {
          const version = row.original.metadata?.classifier_version;
          return (
            <span className="flex items-center">
              {version ? (
                <span className="font-mono text-xs">{version}</span>
              ) : (
                <span className="text-muted-foreground">Unstamped</span>
              )}
              <StalenessBadge rowVersion={version} currentVersion={currentVersion} />
            </span>
          );
        },
      },
    ],
    [currentVersion]
  );

  if (isLoading) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-muted-foreground">
          Loading classification…
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertTriangle className="h-4 w-4" />
        <AlertTitle>Could not load classification</AlertTitle>
        <AlertDescription>{(error as Error)?.message ?? 'Unknown error'}</AlertDescription>
      </Alert>
    );
  }

  if (!data) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center py-12 text-center">
          <ShieldCheck className="h-16 w-16 text-muted-foreground" />
          <p className="mt-4 text-xl font-semibold">Not classified yet</p>
          <p className="mt-2 max-w-md text-muted-foreground">
            Run classification to detect PII and assign a sensitivity level to each
            column in this dataset.
          </p>
          <Button
            className="mt-5"
            onClick={runReclassify}
            disabled={reclassify.isPending}
          >
            <ShieldCheck className="mr-2 h-4 w-4" />
            Classify Dataset
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {staleCount > 0 && (
        <Alert variant="warning">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>
            {staleCount} of {columns.length} columns were classified by an older
            classifier
          </AlertTitle>
          <AlertDescription>
            The current classifier is{' '}
            <span className="font-mono text-xs">{currentVersion}</span>. Re-classify to
            bring these columns up to date.
          </AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
          <div>
            <CardTitle>Classification</CardTitle>
            <CardDescription>
              Stored per-column verdicts. Current classifier{' '}
              <span className="font-mono text-xs">{currentVersion}</span>
            </CardDescription>
          </div>
          <Button
            variant="outline"
            onClick={runReclassify}
            disabled={reclassify.isPending}
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            {reclassify.isPending ? 'Re-classifying…' : 'Re-classify'}
          </Button>
        </CardHeader>

        <CardContent className="space-y-6">
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
            <div>
              <div className="text-xs text-muted-foreground">Overall classification</div>
              <Badge
                variant="outline"
                className={`mt-1 ${toneFor(data.overall_classification)}`}
              >
                {titleCase(data.overall_classification)}
              </Badge>
            </div>
            <div>
              <div className="text-xs text-muted-foreground">Overall sensitivity</div>
              <Badge
                variant="outline"
                className={`mt-1 ${toneFor(data.overall_sensitivity)}`}
              >
                {titleCase(data.overall_sensitivity)}
              </Badge>
            </div>
            <div>
              <div className="text-xs text-muted-foreground">PII columns</div>
              <div className="text-2xl font-bold">{data.pii_columns?.length ?? 0}</div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground">Stale columns</div>
              <div
                data-testid="stale-column-count"
                className={`text-2xl font-bold ${staleCount > 0 ? 'text-warning' : ''}`}
              >
                {staleCount}
              </div>
            </div>
          </div>

          <DataTable data={columns} columns={tableColumns} />
        </CardContent>
      </Card>
    </div>
  );
}
